import { useState, useEffect, useCallback, useMemo } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { CheckCircle, Clock, Warning, Trash, SortAscending, SortDescending, CaretUpDown, PencilSimple, X, FloppyDisk, CalendarBlank, ListBullets, Eye, ChatCircle } from "@phosphor-icons/react";
import { useUnsavedGuard, loadDraft, clearDraft } from "../hooks/useUnsavedGuard";
import TaskCalendar from "../components/tasks/TaskCalendar";
import TaskDetailDrawer from "../components/tasks/TaskDetailDrawer";
import TaskMultiAssignSelect from "../components/tasks/TaskMultiAssignSelect";

const TASKS_VIEW_KEY = "swt_tasks_view_mode"; // localStorage: "list" | "calendar"
const TASKS_CAL_VIEW_KEY = "swt_tasks_calendar_view"; // "month" | "week" | "day"

const PRIORITY_GRADES = [
  { value: "P0", label: "P0 · Critical", color: "#DC2626" },
  { value: "P1", label: "P1 · High", color: "#F59E0B" },
  { value: "P2", label: "P2 · Medium", color: "#002FA7" },
  { value: "P3", label: "P3 · Low", color: "#64748B" },
];

const GRADE_COLOR = Object.fromEntries(PRIORITY_GRADES.map(g => [g.value, g.color]));

// Phase 1 — Task Timing constants
const REMINDER_OPTIONS = [
  { value: "", label: "No reminder" },
  { value: "10m", label: "10 minutes before" },
  { value: "30m", label: "30 minutes before" },
  { value: "1h",  label: "1 hour before" },
  { value: "1d",  label: "1 day before" },
];
const RELATED_MODULES = [
  { value: "", label: "—" },
  { value: "lead", label: "Lead" },
  { value: "deal", label: "Deal" },
  { value: "quotation", label: "Quotation" },
  { value: "invoice", label: "Invoice" },
  { value: "service", label: "Service" },
  { value: "amc", label: "AMC" },
  { value: "general", label: "General" },
];
const VIEW_PRESETS = [
  { value: "", label: "All tasks" },
  { value: "due_today", label: "Due Today" },
  { value: "overdue", label: "Overdue" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "this_week", label: "This Week" },
  { value: "assigned_to_me", label: "Assigned to Me" },
  { value: "assigned_by_me", label: "Assigned by Me" },
];

// Format a task's due date+time for display: "14 May 2026 · 11:30 AM" or "—"
const fmtDue = (task) => {
  if (!task?.due_date) return "—";
  const d = new Date(`${task.due_date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return task.due_date;
  const datePart = `${String(d.getDate()).padStart(2, "0")} ${d.toLocaleString("en-IN", { month: "short" })} ${d.getFullYear()}`;
  if (!task.due_time) return datePart;
  const [hh, mm] = String(task.due_time).split(":");
  const h = Number(hh); const ampm = h >= 12 ? "PM" : "AM"; const h12 = ((h % 12) || 12);
  return `${datePart} · ${h12}:${mm} ${ampm}`;
};

const CRMTasks = ({ user, logout }) => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTask, setEditingTask] = useState(null); // { original, form, dirty }
  const [filterStatus, setFilterStatus] = useState("");
  const [filterGrade, setFilterGrade] = useState("");
  const [filterView, setFilterView] = useState("");
  const [sortBy, setSortBy] = useState("priority_grade");
  const [sortDir, setSortDir] = useState("asc");
  // Phase 2A — view-mode (list/calendar) persisted per-user via localStorage; calendar internal view (month/week/day) persisted too.
  const [viewMode, setViewMode] = useState(() => { try { return localStorage.getItem(TASKS_VIEW_KEY) || "list"; } catch (e) { return "list"; } });
  const [calendarView, setCalendarView] = useState(() => { try { return localStorage.getItem(TASKS_CAL_VIEW_KEY) || "month"; } catch (e) { return "month"; } });
  useEffect(() => { try { localStorage.setItem(TASKS_VIEW_KEY, viewMode); } catch (e) { /* ignore */ } }, [viewMode]);
  useEffect(() => { try { localStorage.setItem(TASKS_CAL_VIEW_KEY, calendarView); } catch (e) { /* ignore */ } }, [calendarView]);
  // Phase 2A — Detail drawer
  const [drawerTaskId, setDrawerTaskId] = useState("");
  const [drawerInitialTab, setDrawerInitialTab] = useState("overview");
  const openDrawer = (taskId, tab = "overview") => {
    setDrawerInitialTab(tab);
    setDrawerTaskId(taskId);
  };
  const [newTask, setNewTask] = useState({
    title: "", description: "", priority_grade: "P2", priority: "medium",
    assigned_to: user?.name || "", assigned_to_id: user?.user_id || "",
    related_contact: "", related_deal: "", due_date: "", created_by: user?.user_id || "",
    // Phase 1 — task timing fields
    due_time: "", reminder_offset: "", related_module: "", related_id: "",
    // Phase 2A — multi-assignee (array of user_ids)
    assignee_ids: [],
  });

  const canAssignAll = ["admin", "manager", "bdm"].includes(user?.role);

  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        requester_role: user?.role || "",
        requester_name: user?.name || "",
        requester_user_id: user?.user_id || "",
      });
      if (filterView) params.append("view", filterView);
      const [tRes, uRes] = await Promise.all([
        api.get(`${API}/crm/tasks?${params.toString()}`),
        canAssignAll ? api.get(`/users`) : Promise.resolve({ data: [] })
      ]);
      setTasks(tRes.data);
      setUsers(uRes.data.filter(u => u.role !== "customer"));
    } catch (error) { toast.error("Failed to fetch tasks"); }
    finally { setLoading(false); }
  }, [user?.role, user?.name, user?.user_id, filterView, canAssignAll]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`${API}/crm/tasks`, {
        ...newTask,
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      toast.success("Task created");
      setShowCreate(false);
      setNewTask({ title: "", description: "", priority_grade: "P2", priority: "medium", assigned_to: user?.name || "", assigned_to_id: user?.user_id || "", related_contact: "", related_deal: "", due_date: "", created_by: user?.user_id || "", due_time: "", reminder_offset: "", related_module: "", related_id: "", assignee_ids: [] });
      fetchTasks();
    } catch (error) { toast.error("Failed to create task"); }
  };

  const updateTask = async (taskId, patch) => {
    try {
      await api.put(`${API}/crm/tasks/${taskId}`, patch);
      fetchTasks();
    } catch (error) { toast.error("Update failed"); }
  };

  const openEdit = (task) => {
    const draftKey = `draft-task-edit-${task.task_id}`;
    const draft = loadDraft(draftKey);
    const base = {
      title: task.title || "",
      description: task.description || "",
      priority_grade: task.priority_grade || "P2",
      status: task.status || "pending",
      assigned_to: task.assigned_to || "",
      assigned_to_id: task.assigned_to_id || "",
      due_date: task.due_date || "",
    };
    if (draft) {
      toast.info("Loaded your unsaved draft");
      setEditingTask({ original: task, form: { ...base, ...draft }, draftKey, fromDraft: true });
    } else {
      setEditingTask({ original: task, form: base, draftKey, fromDraft: false });
    }
  };

  const saveEdit = async () => {
    if (!editingTask) return;
    try {
      await api.put(`${API}/crm/tasks/${editingTask.original.task_id}`, editingTask.form);
      toast.success("Task updated");
      clearDraft(editingTask.draftKey);
      setEditingTask(null);
      fetchTasks();
    } catch (error) { toast.error("Failed to update task"); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`${API}/crm/tasks/${taskId}`);
      toast.success("Task deleted");
      fetchTasks();
    } catch (error) { toast.error("Failed to delete task"); }
  };

  // Dirty detection for edit modal
  const editDirty = useMemo(() => {
    if (!editingTask) return false;
    const { original, form } = editingTask;
    return (
      form.title !== (original.title || "") ||
      form.description !== (original.description || "") ||
      form.priority_grade !== (original.priority_grade || "P2") ||
      form.status !== (original.status || "pending") ||
      form.assigned_to_id !== (original.assigned_to_id || "") ||
      form.due_date !== (original.due_date || "")
    );
  }, [editingTask]);

  const guard = useUnsavedGuard({
    isDirty: editDirty,
    draftKey: editingTask?.draftKey,
    draftData: editingTask?.form,
    onSaveDraft: () => { toast.success("Draft saved. Reopen the task to resume."); setEditingTask(null); },
    onDiscard: () => { setEditingTask(null); },
  });

  const getStatusIcon = (status) => {
    if (status === "completed") return <CheckCircle size={18} weight="fill" className="text-[#10B981]" />;
    if (status === "in_progress") return <Clock size={18} weight="fill" className="text-[#002FA7]" />;
    return <Warning size={18} weight="fill" className="text-[#F59E0B]" />;
  };

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
  };

  const sorted = useMemo(() => {
    let list = [...tasks];
    if (filterStatus) list = list.filter(t => t.status === filterStatus);
    if (filterGrade) list = list.filter(t => (t.priority_grade || "P2") === filterGrade);
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av, bv;
      if (sortBy === "priority_grade") { av = a.priority_grade || "P3"; bv = b.priority_grade || "P3"; }
      else if (sortBy === "due_date") { av = a.due_date || "9999"; bv = b.due_date || "9999"; }
      else if (sortBy === "title") { av = (a.title || "").toLowerCase(); bv = (b.title || "").toLowerCase(); }
      else { av = a.created_at || ""; bv = b.created_at || ""; }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return list;
  }, [tasks, filterStatus, filterGrade, sortBy, sortDir]);

  const SortHeader = ({ field, label }) => (
    <button onClick={() => toggleSort(field)} className="flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-bold text-slate-500 hover:text-[#002FA7] transition-colors duration-300">
      {label}
      {sortBy === field ? (sortDir === "asc" ? <SortAscending size={14} weight="bold" /> : <SortDescending size={14} weight="bold" />) : <CaretUpDown size={12} />}
    </button>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        {/* STICKY HEADER */}
        <div className="page-header-sticky">
          <div className="px-8 py-5 flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center"><h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{fontFamily: 'Outfit, sans-serif'}}>Tasks</h1><HelpButton moduleKey="follow_up" /></div>
              <p className="text-xs text-slate-500 uppercase tracking-[0.2em] mt-1">{sorted.length} tasks · sorted by {sortBy.replace('_', ' ')}</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <select value={filterView} onChange={e => setFilterView(e.target.value)} data-testid="task-filter-view"
                className="rounded-none border border-slate-300 text-sm p-2 outline-none bg-white">
                {VIEW_PRESETS.map(v => <option key={v.value || 'all'} value={v.value}>{v.label}</option>)}
              </select>
              <select value={filterGrade} onChange={e => setFilterGrade(e.target.value)} data-testid="task-filter-grade"
                className="rounded-none border border-slate-300 text-sm p-2 outline-none">
                <option value="">All Priorities</option>
                {PRIORITY_GRADES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-testid="task-filter-status"
                className="rounded-none border border-slate-300 text-sm p-2 outline-none">
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <div className="inline-flex border border-slate-300" data-testid="tasks-view-toggle">
                <button data-testid="tasks-view-list" onClick={() => setViewMode("list")}
                  className={`text-xs uppercase tracking-wider font-bold px-3 py-2 inline-flex items-center gap-1 ${viewMode === "list" ? "bg-[#002FA7] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                  <ListBullets size={12} /> List
                </button>
                <button data-testid="tasks-view-calendar" onClick={() => setViewMode("calendar")}
                  className={`text-xs uppercase tracking-wider font-bold px-3 py-2 inline-flex items-center gap-1 ${viewMode === "calendar" ? "bg-[#002FA7] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
                  <CalendarBlank size={12} /> Calendar
                </button>
              </div>
              <button data-testid="create-task-btn" onClick={() => setShowCreate(!showCreate)}
                className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium transition-colors duration-300">
                {showCreate ? 'Cancel' : '+ New Task'}
              </button>
            </div>
          </div>
        </div>

        <div className="p-8">
          {showCreate && (
            <div data-testid="create-task-form" className="bg-white border border-slate-200 rounded-none p-8 mb-8 shadow-sm">
              <h3 className="text-xl font-semibold mb-6" style={{fontFamily: 'Outfit, sans-serif'}}>New Task</h3>
              <form onSubmit={handleCreate} className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Title</label>
                  <input data-testid="task-title-input" type="text" value={newTask.title} onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
                </div>
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Description</label>
                  <textarea data-testid="task-description-input" value={newTask.description} onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" rows="3" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Priority Grade</label>
                  <select data-testid="task-priority-grade-select" value={newTask.priority_grade}
                    onChange={(e) => setNewTask({...newTask, priority_grade: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                    {PRIORITY_GRADES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Due Date</label>
                  <input data-testid="task-due-date-input" type="date" value={newTask.due_date} onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Due Time (IST)</label>
                  <input data-testid="task-due-time-input" type="time" value={newTask.due_time} onChange={(e) => setNewTask({...newTask, due_time: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Reminder Before</label>
                  <select data-testid="task-reminder-select" value={newTask.reminder_offset}
                    onChange={(e) => setNewTask({...newTask, reminder_offset: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                    {REMINDER_OPTIONS.map(r => <option key={r.value || 'none'} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Related Module</label>
                  <select data-testid="task-related-module-select" value={newTask.related_module}
                    onChange={(e) => setNewTask({...newTask, related_module: e.target.value})}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                    {RELATED_MODULES.map(r => <option key={r.value || 'none'} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {newTask.related_module && (
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Related ID</label>
                    <input data-testid="task-related-id-input" type="text" value={newTask.related_id} onChange={(e) => setNewTask({...newTask, related_id: e.target.value})}
                      placeholder={`Enter ${newTask.related_module} reference id`}
                      className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none" />
                  </div>
                )}
                <div className="col-span-2">
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Assigned To (single)</label>
                  <select data-testid="task-assigned-select" value={newTask.assigned_to_id}
                    onChange={(e) => {
                      const u = users.find(x => x.user_id === e.target.value);
                      setNewTask({...newTask, assigned_to_id: e.target.value, assigned_to: u ? u.name : ""});
                    }}
                    className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                    <option value="">Unassigned</option>
                    {(canAssignAll ? users : users.filter(u => u.user_id === user?.user_id)).map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.name} · {u.role.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
                {canAssignAll && (
                  <div className="col-span-2">
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Assign to multiple users (optional — overrides single)</label>
                    <TaskMultiAssignSelect
                      users={users}
                      value={newTask.assignee_ids}
                      onChange={(ids) => setNewTask({ ...newTask, assignee_ids: ids })}
                      currentUserId={user?.user_id}
                    />
                  </div>
                )}
                <div className="col-span-2">
                  <button data-testid="submit-task-btn" type="submit" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-8 py-3 font-medium transition-colors duration-300">Create Task</button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20"><div className="loading-spinner"></div></div>
          ) : viewMode === "calendar" ? (
            <TaskCalendar
              user={user}
              view={calendarView}
              onViewChange={setCalendarView}
              filters={{ viewPreset: filterView, priorityGrade: filterGrade, relatedModule: "" }}
              onEventClick={(ev) => openDrawer(ev.task_id, "overview")}
            />
          ) : (
            <div className="bg-white border border-slate-200 rounded-none shadow-sm overflow-hidden">
              <table className="w-full" data-testid="tasks-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left w-10"></th>
                    <th className="px-4 py-3 text-left"><SortHeader field="priority_grade" label="Priority" /></th>
                    <th className="px-4 py-3 text-left"><SortHeader field="title" label="Title" /></th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Assigned</th>
                    <th className="px-4 py-3 text-left"><SortHeader field="due_date" label="Due" /></th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Status</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="tasks-list">
                  {sorted.map((task, index) => {
                    const grade = task.priority_grade || "P2";
                    return (
                      <tr key={task.task_id} data-testid={`task-card-${index}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-300">
                        <td className="px-4 py-3">{getStatusIcon(task.status)}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-none text-xs font-bold text-white" style={{ backgroundColor: GRADE_COLOR[grade] || "#64748B" }}>
                            {grade}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium cursor-pointer hover:text-[#002FA7]" data-testid={`task-title-${index}`} onClick={() => openDrawer(task.task_id, "overview")}>{task.title}</div>
                          {task.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{task.description}</div>}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {canAssignAll ? (
                            <select value={task.assigned_to_id || ""}
                              onChange={(e) => {
                                const u = users.find(x => x.user_id === e.target.value);
                                updateTask(task.task_id, { assigned_to_id: e.target.value, assigned_to: u ? u.name : "" });
                              }}
                              data-testid={`task-reassign-${index}`}
                              className="rounded-none border border-slate-200 text-xs p-1 bg-white max-w-[160px]">
                              <option value="">Unassigned</option>
                              {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                            </select>
                          ) : (
                            <span className="text-slate-600">{task.assigned_to || '—'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          <div className="whitespace-nowrap" data-testid={`task-due-${index}`}>{fmtDue(task)}</div>
                          {task.is_overdue && (
                            <span data-testid={`task-overdue-badge-${index}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 mt-1 rounded-none text-[10px] uppercase tracking-wider font-bold bg-red-50 text-red-700 border border-red-200">
                              <Warning size={10} weight="bold" /> Overdue
                            </span>
                          )}
                          {task.related_module && (
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                              {task.related_module}{task.related_id ? ` · ${task.related_id}` : ""}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <select data-testid={`task-status-select-${index}`} value={task.status}
                            onChange={(e) => updateTask(task.task_id, { status: e.target.value })}
                            className="rounded-none bg-white border border-slate-300 text-xs p-1 outline-none">
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button data-testid={`view-task-${index}`} onClick={() => openDrawer(task.task_id, "overview")}
                              className="p-2 hover:bg-slate-100 text-slate-600 transition-colors duration-300" title="View task">
                              <Eye size={16} />
                            </button>
                            <button data-testid={`reply-task-${index}`} onClick={() => openDrawer(task.task_id, "conversation")}
                              className="p-2 hover:bg-[#002FA7]/10 text-[#002FA7] transition-colors duration-300" title="Reply / Conversation">
                              <ChatCircle size={16} />
                            </button>
                            <button data-testid={`edit-task-${index}`} onClick={() => openEdit(task)}
                              className="p-2 hover:bg-blue-50 text-[#002FA7] transition-colors duration-300" title="Edit">
                              <PencilSimple size={16} />
                            </button>
                            <button data-testid={`delete-task-${index}`} onClick={() => deleteTask(task.task_id)}
                              className="p-2 hover:bg-red-50 text-red-500 transition-colors duration-300" title="Delete">
                              <Trash size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {sorted.length === 0 && <div className="text-center py-16 text-slate-400">No tasks match the current filters</div>}
            </div>
          )}
        </div>
      </div>

      {/* EDIT TASK MODAL */}
      {editingTask && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40 flex items-center justify-center p-4" data-testid="edit-task-modal"
          onClick={() => guard.requestClose(() => setEditingTask(null))}>
          <div className="bg-white shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Edit Task</h3>
                {editingTask.fromDraft && <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-2 py-0.5 border border-amber-200 mt-1"><FloppyDisk size={12} /> Draft restored</span>}
                {editDirty && !editingTask.fromDraft && <span className="text-xs text-amber-600">• Unsaved changes</span>}
              </div>
              <button onClick={() => guard.requestClose(() => setEditingTask(null))} className="p-2 hover:bg-slate-100" data-testid="close-edit-task"><X size={18} /></button>
            </div>
            <form onSubmit={e => { e.preventDefault(); saveEdit(); }} className="p-6 space-y-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Title</label>
                <input data-testid="edit-task-title" value={editingTask.form.title}
                  onChange={e => setEditingTask({ ...editingTask, form: { ...editingTask.form, title: e.target.value } })}
                  className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Description</label>
                <textarea data-testid="edit-task-description" value={editingTask.form.description}
                  onChange={e => setEditingTask({ ...editingTask, form: { ...editingTask.form, description: e.target.value } })}
                  className="rounded-none border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" rows="3" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Priority</label>
                  <select data-testid="edit-task-priority" value={editingTask.form.priority_grade}
                    onChange={e => setEditingTask({ ...editingTask, form: { ...editingTask.form, priority_grade: e.target.value } })}
                    className="rounded-none border border-slate-300 p-3 w-full outline-none">
                    {PRIORITY_GRADES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Status</label>
                  <select data-testid="edit-task-status" value={editingTask.form.status}
                    onChange={e => setEditingTask({ ...editingTask, form: { ...editingTask.form, status: e.target.value } })}
                    className="rounded-none border border-slate-300 p-3 w-full outline-none">
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Due Date</label>
                  <input type="date" data-testid="edit-task-due" value={editingTask.form.due_date}
                    onChange={e => setEditingTask({ ...editingTask, form: { ...editingTask.form, due_date: e.target.value } })}
                    className="rounded-none border border-slate-300 p-3 w-full outline-none" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Assigned To</label>
                  <select data-testid="edit-task-assigned" value={editingTask.form.assigned_to_id}
                    onChange={e => {
                      const u = users.find(x => x.user_id === e.target.value);
                      setEditingTask({ ...editingTask, form: { ...editingTask.form, assigned_to_id: e.target.value, assigned_to: u ? u.name : "" } });
                    }}
                    className="rounded-none border border-slate-300 p-3 w-full outline-none">
                    <option value="">Unassigned</option>
                    {(canAssignAll ? users : users.filter(u => u.user_id === user?.user_id)).map(u => (
                      <option key={u.user_id} value={u.user_id}>{u.name} · {u.role.toUpperCase()}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button type="button" onClick={() => guard.requestClose(() => setEditingTask(null))} className="rounded-none border border-slate-300 px-5 py-2.5 hover:bg-slate-50">Cancel</button>
                <button type="submit" data-testid="save-edit-task" className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-2.5 font-bold">Save Task</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Global UnsavedChangesModal is rendered by NavigationGuardProvider */}
      <TaskDetailDrawer
        open={!!drawerTaskId}
        taskId={drawerTaskId}
        user={user}
        initialTab={drawerInitialTab}
        onClose={() => { setDrawerTaskId(""); setDrawerInitialTab("overview"); }}
        onChanged={fetchTasks}
      />
    </div>
  );
};

export default CRMTasks;
